import React, { useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import {
  DeleteOutline,
  DownloadOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";

import * as XLSX from "xlsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LatestUploadCard from "../LatestUploadCard";

const API = process.env.REACT_APP_BACKEND_URL;

const HEADER_MAPPING = {
  "Component number": "materialNo",
  "Object description": "description",
  "Overflow indicator": "overflowIndicator",
  "Comp. Qty (CUn)": "qty",
  "Component UoM": "uom",
  "Item category": "itemCategory",
  Exception: "exception",
  Type: "type",
};

const ITEM_CODE_HEADERS = [
  "item code",
  "battery code",
  "item code/battery code",
];

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalize = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const number = Number(String(value).replace(/,/g, "").trim());

  return Number.isFinite(number) ? number : null;
};

const UploadBom = () => {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentRows, setCurrentRows] = useState([]);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [currentError, setCurrentError] = useState("");

  const getAuth = () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");

    if (!storedUser?.accessToken) {
      throw new Error("You are not logged in.");
    }

    return {
      headers: {
        Authorization: `Bearer ${storedUser.accessToken}`,
      },
    };
  };

  // Load the BOM currently stored, one row per item component.
  const loadCurrent = async () => {
    try {
      setCurrentLoading(true);
      setCurrentError("");

      const response = await axios.get(`${API}/bom/item/all`, getAuth());

      setCurrentRows(
        (response.data || []).map((row, index) => ({
          id: row.itemId || index + 1,
          item: row.item,
          materialNo: row.bomMaterial?.materialNo || row.subAssembly,
          description: row.bomMaterial?.description,
          qty: row.qty,
          uom: row.bomMaterial?.uom,
          itemCategory: row.bomMaterial?.itemCategory,
          type: row.bomMaterial?.type,
          updatedDate: row.updatedDate || row.createdDate,
        })),
      );
    } catch (err) {
      setCurrentRows([]);
      setCurrentError(
        err?.response?.data?.Exception ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to load the current BOM.",
      );
    } finally {
      setCurrentLoading(false);
    }
  };

  useEffect(() => {
    loadCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentItemCount = new Set(currentRows.map((row) => row.item)).size;

  const currentUpdatedAt = currentRows.reduce(
    (latest, row) =>
      row.updatedDate &&
      (!latest || new Date(row.updatedDate) > new Date(latest))
        ? row.updatedDate
        : latest,
    null,
  );

  const currentColumns = [
    { field: "item", headerName: "Item Code", minWidth: 140, flex: 1 },
    { field: "materialNo", headerName: "Component", minWidth: 140, flex: 1 },
    {
      field: "description",
      headerName: "Description",
      minWidth: 240,
      flex: 1.8,
    },
    { field: "qty", headerName: "Qty", minWidth: 90 },
    { field: "uom", headerName: "UoM", minWidth: 80 },
    { field: "itemCategory", headerName: "Item Category", minWidth: 120 },
    { field: "type", headerName: "Type", minWidth: 100 },
    {
      field: "updatedDate",
      headerName: "Last Updated",
      minWidth: 170,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleString("en-IN") : "—",
    },
  ];

  const downloadTemplate = () => {
    const headers = [...Object.keys(HEADER_MAPPING), "Item code/Battery code"];

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "BOM Template");

    XLSX.writeFile(workbook, "BOM_Upload_Template.xlsx");
  };

  const parseFile = async (file) => {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      throw new Error(
        `${file.name}: Please select an Excel file (.xlsx or .xls).`,
      );
    }

    const workbook = XLSX.read(await file.arrayBuffer(), {
      type: "array",
    });

    if (!workbook.SheetNames.length) {
      throw new Error(`${file.name}: No worksheet found.`);
    }

    const worksheet = workbook.Sheets[workbook.SheetNames[0]];

    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      raw: true,
    });

    if (rows.length < 2) {
      throw new Error(`${file.name}: The worksheet contains no BOM data rows.`);
    }

    const headers = rows[0].map(normalizeHeader);
    const indexes = {};
    const missingHeaders = [];

    Object.keys(HEADER_MAPPING).forEach((header) => {
      const index = headers.indexOf(normalizeHeader(header));

      if (index === -1) {
        missingHeaders.push(header);
      } else {
        indexes[header] = index;
      }
    });

    const itemCodeIndex = headers.findIndex((header) =>
      ITEM_CODE_HEADERS.includes(header),
    );

    if (itemCodeIndex === -1) {
      missingHeaders.push("Item code/Battery code, Item code, or Battery code");
    } else {
      indexes["Item code/Battery code"] = itemCodeIndex;
    }

    if (missingHeaders.length > 0) {
      throw new Error(
        `${file.name}: Missing required headers: ${missingHeaders.join(", ")}`,
      );
    }

    const foundItemCodes = new Set();
    const parsedRows = [];

    rows.slice(1).forEach((row, index) => {
      const excelRow = index + 2;

      const materialNo = normalize(row[indexes["Component number"]]);
      const description = normalize(row[indexes["Object description"]]);
      const qty = toNumber(row[indexes["Comp. Qty (CUn)"]]);
      const uom = normalize(row[indexes["Component UoM"]]);
      const rowItemCode = normalize(row[itemCodeIndex]);

      if (rowItemCode) {
        foundItemCodes.add(rowItemCode);
      }

      const emptyRow =
        !materialNo &&
        !description &&
        qty === null &&
        !uom &&
        !normalize(row[indexes["Type"]]) &&
        !normalize(row[indexes["Overflow indicator"]]) &&
        !normalize(row[indexes["Item category"]]) &&
        !normalize(row[indexes["Exception"]]) &&
        !rowItemCode;

      if (emptyRow) {
        return;
      }

      if (!materialNo) {
        throw new Error(
          `${file.name}, row ${excelRow}: Component number is required.`,
        );
      }

      if (qty === null || qty < 0) {
        throw new Error(
          `${file.name}, row ${excelRow}: Comp. Qty (CUn) must be a valid non-negative number.`,
        );
      }

      if (!uom) {
        throw new Error(
          `${file.name}, row ${excelRow}: Component UoM is required.`,
        );
      }

      parsedRows.push({
        materialNo,
        description,
        overflowIndicator: normalize(row[indexes["Overflow indicator"]]),
        qty,
        uom,
        itemCategory: normalize(row[indexes["Item category"]]),
        exception: normalize(row[indexes["Exception"]]),
        type: normalize(row[indexes["Type"]]),
        item: "",
        subAssembly: null,
        sourceFile: file.name,
      });
    });

    if (!parsedRows.length) {
      throw new Error(`${file.name}: No valid BOM component rows were found.`);
    }

    if (foundItemCodes.size === 0) {
      throw new Error(
        `${file.name}: No item code was found. Fill the Item code or Battery code column.`,
      );
    }

    if (foundItemCodes.size > 1) {
      throw new Error(
        `${file.name}: Multiple item codes were found. Upload one BOM item code per file.`,
      );
    }

    const itemCode = [...foundItemCodes][0];

    parsedRows.forEach((row) => {
      row.item = itemCode;
    });

    return {
      file,
      item: itemCode,
      rows: parsedRows,
    };
  };

  const loadSummary = async (parsedFiles) => {
    const response = await axios.get(`${API}/bom/item/all`, getAuth());

    const oldRows = response.data || [];

    return parsedFiles.map(({ file, item, rows }) => {
      const existingRows = oldRows.filter(
        (old) => normalize(old.item).toLowerCase() === item.toLowerCase(),
      );

      const oldByMaterial = new Map();

      existingRows.forEach((old) => {
        const materialNo = normalize(
          old.bomMaterial?.materialNo || old.subAssembly,
        );

        if (materialNo) {
          if (!oldByMaterial.has(materialNo)) {
            oldByMaterial.set(materialNo, []);
          }

          oldByMaterial.get(materialNo).push(old);
        }
      });

      const newByMaterial = new Map();

      rows.forEach((row) => {
        if (!newByMaterial.has(row.materialNo)) {
          newByMaterial.set(row.materialNo, []);
        }

        newByMaterial.get(row.materialNo).push(row);
      });

      let added = 0;
      let removed = 0;
      let changed = 0;

      newByMaterial.forEach((newLines, materialNo) => {
        const oldLines = oldByMaterial.get(materialNo) || [];

        if (!oldLines.length) {
          added += newLines.length;
          return;
        }

        newLines.forEach((newLine, index) => {
          const oldLine = oldLines[index];

          if (!oldLine) {
            added += 1;
          } else if (Number(oldLine.qty) !== Number(newLine.qty)) {
            changed += 1;
          }
        });
      });

      oldByMaterial.forEach((oldLines, materialNo) => {
        const newLines = newByMaterial.get(materialNo) || [];

        if (!newLines.length) {
          removed += oldLines.length;
        } else if (oldLines.length > newLines.length) {
          removed += oldLines.length - newLines.length;
        }
      });

      return {
        id: `${item}-${file.name}`,
        fileName: file.name,
        item,
        status: existingRows.length ? "Editing this item" : "New item",
        components: rows.length,
        added,
        removed,
        changed,
      };
    });
  };

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) {
      return;
    }

    setError("");
    setSuccess("");
    setFiles([]);
    setRequests([]);
    setSummary([]);
    setLoading(true);

    try {
      const parsedFiles = [];
      const seenItems = new Set();

      for (const file of selectedFiles) {
        const parsed = await parseFile(file);
        const itemKey = parsed.item.toLowerCase();

        if (seenItems.has(itemKey)) {
          throw new Error(
            `Item code "${parsed.item}" occurs in more than one selected file. Please select only one file per item code.`,
          );
        }

        seenItems.add(itemKey);
        parsedFiles.push(parsed);
      }

      const nextSummary = await loadSummary(parsedFiles);
      const allRequests = parsedFiles.flatMap((parsed) => parsed.rows);

      setFiles(selectedFiles);
      setRequests(allRequests);
      setSummary(nextSummary);

      setSuccess(
        `${selectedFiles.length} Excel file(s) processed successfully.`,
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to process the selected files.",
      );
    } finally {
      setLoading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSubmit = async () => {
    if (!requests.length) {
      setError("Please select BOM Excel files first.");
      return;
    }

    setError("");
    setSuccess("");
    setUploading(true);

    try {
      const payload = requests.map(({ sourceFile, ...request }) => request);

      const response = await axios.post(`${API}/bom`, payload, getAuth());

      setSuccess(response.data || "BOM data uploaded successfully.");

      navigate("/admin");
    } catch (err) {
      setError(
        err?.response?.data?.Exception ||
          err?.response?.data?.message ||
          err?.message ||
          "BOM upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFiles([]);
    setRequests([]);
    setSummary([]);
    setError("");
    setSuccess("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "#172033", mb: 1 }}
        >
          Upload BOM
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Select multiple Excel files to upload BOM data.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, whiteSpace: "pre-line" }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <LatestUploadCard
          title="Current BOM"
          uploadedAt={currentUpdatedAt}
          uploadedAtLabel="Last updated on"
          stats={[
            { label: "Items", value: currentItemCount },
            { label: "Components", value: currentRows.length },
            {
              label: "Unique materials",
              value: new Set(
                currentRows.map((row) => row.materialNo).filter(Boolean),
              ).size,
            },
          ]}
          loading={currentLoading}
          error={currentError}
          isEmpty={currentRows.length === 0}
          emptyMessage="No BOM has been uploaded yet."
          onRefresh={loadCurrent}
          rows={currentRows}
          columns={currentColumns}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid #e3e7ef",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              BOM Excel files
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Required columns: Component number, Object description, Overflow
              indicator, Comp. Qty (CUn), Component UoM, Item category,
              Exception, Type, and either Item code or Battery code. Extra
              columns are ignored.
            </Typography>

            {files.length > 0 && (
              <Typography sx={{ mt: 1, fontWeight: 600 }}>
                {files.length} file(s) selected
              </Typography>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlined />}
              onClick={downloadTemplate}
              disabled={loading || uploading}
            >
              Download Excel Template
            </Button>

            <Button
              variant="outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading}
            >
              {loading ? "Processing..." : "Choose Excel Files"}
            </Button>

            {requests.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={handleClear}
                disabled={uploading}
              >
                Clear
              </Button>
            )}
          </Stack>

          {files.length > 0 && (
            <List dense disablePadding>
              {files.map((file) => (
                <ListItem key={`${file.name}-${file.size}`} disableGutters>
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Paper>

      {summary.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: "1px solid #e3e7ef",
            borderRadius: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 2 }}>
            Upload Summary
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Chip label={`Files: ${files.length}`} variant="outlined" />

            <Chip label={`Items: ${summary.length}`} variant="outlined" />

            <Chip label={`Components: ${requests.length}`} variant="outlined" />
          </Stack>

          {summary.map((entry, index) => (
            <Box key={entry.id}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ md: "center" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{entry.item}</Typography>

                  <Typography variant="body2" color="text.secondary">
                    {entry.fileName} · {entry.components} components
                  </Typography>
                </Box>

                <Chip
                  color={entry.status === "New item" ? "success" : "warning"}
                  label={entry.status}
                  variant="outlined"
                />

                {entry.status !== "New item" && (
                  <Typography variant="body2" color="text.secondary">
                    Added: {entry.added} · Removed: {entry.removed}
                    {" · "}Qty changed: {entry.changed}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}

          <Button
            sx={{ mt: 2 }}
            className="btn-contained"
            startIcon={
              uploading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <UploadFileOutlined />
              )
            }
            onClick={handleSubmit}
            disabled={uploading || loading || requests.length === 0}
          >
            {uploading ? "Saving BOM..." : "Submit All BOM Files"}
          </Button>
        </Paper>
      )}

    </Box>
  );
};

export default UploadBom;
